/**
 * Payroll periods — the week payroll is actually run for.
 *
 * J&J runs a WEEKLY cycle from Wednesday to Tuesday. Verified against NetSuite
 * itself: live batch 01051 was "period ending 8/18/2026, Weekly", and
 * 2026-08-18 is a Tuesday, so that period ran Wed 2026-08-12 → Tue 2026-08-18.
 *
 * Because the cycle is WEEKLY, no anchor date is needed — the day of the week
 * alone determines which period a date belongs to, since every Wednesday starts
 * one. (An anchor would only be required for a bi-weekly or monthly cycle, where
 * consecutive weeks are not interchangeable.)
 *
 * Everything here is PURE and operates on ISO `YYYY-MM-DD` strings compared
 * lexicographically, with all arithmetic in UTC — so the same date resolves to
 * the same period in the browser and on the server, regardless of either one's
 * timezone. What "today" is remains the caller's decision, and the API supplies
 * it from the PAYROLL timezone.
 */
/**
 * Day of the week a payroll period starts on: Wednesday (0 = Sunday).
 *
 * A named constant rather than tenant configuration (Michael, 2026-08-19): one
 * line to change if the cycle ever moves, and it stays covered by tests.
 */
export const PAYROLL_WEEK_START_DAY = 3;
/** Days in one payroll period. */
export const PAYROLL_PERIOD_DAYS = 7;
const MS_PER_DAY = 86_400_000;
/** Epoch ms (UTC midnight) for an ISO date. */
function toUtcMs(iso) {
    return Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
}
/** ISO `YYYY-MM-DD` for an epoch ms. */
function toIso(ms) {
    return new Date(ms).toISOString().slice(0, 10);
}
function makePeriod(startMs) {
    const startDate = toIso(startMs);
    const endDate = toIso(startMs + (PAYROLL_PERIOD_DAYS - 1) * MS_PER_DAY);
    return { startDate, endDate, label: `${startDate} – ${endDate}` };
}
/**
 * The payroll period a date falls in.
 *
 * Walks back to the most recent {@link PAYROLL_WEEK_START_DAY}; a date that IS
 * that weekday starts its own period.
 */
export function payrollPeriodFor(date) {
    const ms = toUtcMs(date);
    const dayOfWeek = new Date(ms).getUTCDay();
    const daysIn = (dayOfWeek - PAYROLL_WEEK_START_DAY + 7) % 7;
    return makePeriod(ms - daysIn * MS_PER_DAY);
}
/**
 * The period `offset` periods away from `period` — negative for earlier.
 * `shiftPayrollPeriod(p, -1)` is the week before.
 */
export function shiftPayrollPeriod(period, offset) {
    return makePeriod(toUtcMs(period.startDate) + offset * PAYROLL_PERIOD_DAYS * MS_PER_DAY);
}
/**
 * Every payroll period overlapping an inclusive date range, in order.
 *
 * Returns an empty list when the range is inverted. Used when something spans a
 * period boundary — a multi-day shutdown, say — and each period must be handled
 * separately.
 */
export function payrollPeriodsCovering(startDate, endDate) {
    if (endDate < startDate) {
        return [];
    }
    const periods = [];
    let current = payrollPeriodFor(startDate);
    // Bounded by the range itself: each step advances exactly one week.
    while (current.startDate <= endDate) {
        periods.push(current);
        current = shiftPayrollPeriod(current, 1);
    }
    return periods;
}
/** True when `date` falls inside `period` (inclusive). */
export function isInPayrollPeriod(period, date) {
    return date >= period.startDate && date <= period.endDate;
}
/**
 * The overlap between an inclusive date range and a payroll period, or `null`
 * when they do not intersect.
 *
 * This is what stops a holiday that straddles two periods being paid twice: each
 * period pays only the days that belong to it.
 */
export function clampToPayrollPeriod(range, period) {
    const startDate = range.startDate > period.startDate ? range.startDate : period.startDate;
    const endDate = range.endDate < period.endDate ? range.endDate : period.endDate;
    return endDate < startDate ? null : { startDate, endDate };
}
/**
 * The pay period that has most recently CLOSED, as of `today`.
 *
 * 🔥 THIS IS NOT `payrollPeriodFor(today)`, and the difference is a whole week of pay.
 * Payroll is worked on AFTER the week ends — on the Wednesday or Thursday, which
 * already sit inside the NEXT period. So `payrollPeriodFor(today)` names a week that
 * has barely started and that nobody is paying yet.
 *
 * Michael, 2026-09-24: the allowance sweep used today's period and wrote thirty
 * entries against the week ending 09-29 while payroll was being run for the week
 * ending 09-22. Nothing was paid wrongly — but the entries landed where nobody would
 * look for them, and a week later they would have been mistaken for that week's.
 *
 * ON A TUESDAY (the last day of a period, when the automatic sweep fires) this returns
 * that same Tuesday, so the automatic and manual answers agree rather than differing
 * by a week depending on which day somebody pressed the button.
 *
 * @param today local `YYYY-MM-DD` in the payroll time zone.
 */
export function closedPayrollPeriod(today) {
    const current = payrollPeriodFor(today);
    // endDate is the period's Tuesday. If it has not arrived yet, the period is still
    // running and the CLOSED one is the period before it.
    return current.endDate <= today ? current : shiftPayrollPeriod(current, -1);
}
/** Convenience: just the ending date of {@link closedPayrollPeriod}. */
export function closedPayrollPeriodEnding(today) {
    return closedPayrollPeriod(today).endDate;
}
//# sourceMappingURL=payroll-period.js.map